import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './BrandHeroSection.css';
import { images } from '../../shared/assets';
import { useBrandHomePageImages, useBrandHomePageContent } from '../../../hooks/useBrandHomePageContent';


interface VolumeButtonProps {
  onClick?: () => void;
  isMuted: boolean;
}

const VolumeButton: React.FC<VolumeButtonProps> = ({ onClick, isMuted }) => {
  const { t } = useTranslation();
  return (
    <button className="brand-hero-section__volume-button" onClick={onClick} aria-label={t('pages/home:hero.volumeAria')}>
      <svg
        className="brand-hero-section__volume-svg"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        overflow="visible"
        style={{ display: 'block' }}
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {isMuted ? (
          <path
            id="volume_off"
            d="M16.7273 18L13.9773 15.25C13.5985 15.4924 13.197 15.7008 12.7727 15.875C12.3485 16.0492 11.9091 16.1894 11.4545 16.2955V14.4318C11.6667 14.3561 11.875 14.2803 12.0795 14.2045C12.2841 14.1288 12.4773 14.0379 12.6591 13.9318L9.63636 10.9091V15.6364L5.09091 11.0909H1.45455V5.63636H4.36364L0 1.27273L1.27273 0L18 16.7273L16.7273 18ZM16.5455 12.7273L15.2273 11.4091C15.4849 10.9394 15.678 10.447 15.8068 9.93182C15.9356 9.41667 16 8.88636 16 8.34091C16 6.91667 15.5833 5.64394 14.75 4.52273C13.9167 3.40152 12.8182 2.64394 11.4545 2.25V0.386364C13.3333 0.810606 14.8636 1.76136 16.0455 3.23864C17.2273 4.71591 17.8182 6.41667 17.8182 8.34091C17.8182 9.14394 17.7083 9.91667 17.4886 10.6591C17.2689 11.4015 16.9545 12.0909 16.5455 12.7273ZM13.5 9.68182L11.4545 7.63636V4.68182C12.1667 5.01515 12.7235 5.51515 13.125 6.18182C13.5265 6.84849 13.7273 7.57576 13.7273 8.36364C13.7273 8.59091 13.7083 8.81439 13.6705 9.03409C13.6326 9.25379 13.5758 9.4697 13.5 9.68182ZM9.63636 5.81818L7.27273 3.45455L9.63636 1.09091V5.81818Z"
            fill="white"
          />
        ) : (
          <path
            id="volume_on"
            d="M11.1818 17.8182V15.9545C12.5455 15.5455 13.6439 14.7765 14.4773 13.6477C15.3106 12.5189 15.7273 11.2424 15.7273 9.81818C15.7273 8.39394 15.3106 7.11364 14.4773 5.97727C13.6439 4.84091 12.5455 4.0697 11.1818 3.66364V1.8C13.0606 2.25455 14.5909 3.20076 15.7727 4.63864C16.9545 6.07652 17.5455 7.73788 17.5455 9.62273C17.5455 11.5303 16.9545 13.2121 15.7727 14.6682C14.5909 16.1242 13.0606 17.0758 11.1818 17.5227V17.8182ZM0 11.1818V6.63636H4.54545L9.09091 2.09091V15.7273L4.54545 11.1818H0ZM11.1818 13.1818V6.29545C11.8939 6.57576 12.4508 7.05303 12.8523 7.72727C13.2538 8.40152 13.4545 9.14394 13.4545 9.95455C13.4545 10.7424 13.2538 11.4735 12.8523 12.1477C12.4508 12.822 11.8939 13.2879 11.1818 13.5455V13.1818ZM7.27273 5.59091L4.70455 8.18182H1.81818V9.63636H4.70455L7.27273 12.2273V5.59091Z"
            fill="white"
          />
        )}
      </svg>
    </button>
  );
};

export const BrandHeroSection: React.FC = () => {
  const { t } = useTranslation();
  const { hero: heroImages, loading: imagesLoading } = useBrandHomePageImages();
  const { hero: heroContent, loading: contentLoading } = useBrandHomePageContent();
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Use Contentful images if available, fallback to static assets
  const heroVideoSrc = heroImages?.backgroundVideo || "https://videos.ctfassets.net/hz1mviiqoqof/QgxifSyASmczemC0h46Pm/7a3b799704739fbaae22626ecea1b7ff/Ð_Ð_Ñ_Ð_ÐµÐ_Ð_Ð_Ð_Ñ_ÐµÐ_Ñ_Ð_Ð_Ñ_Ñ_Ñ__01.mp4";
  const heroImageSrc = heroImages?.mastheadImage || "/assets/images/masthead-tree-video.jpg";
  const heroTitle = heroContent?.title || t('pages/home:hero.title');

  const handleVolumeToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section className="brand-hero-section" data-scroll-section>
      {/* Background Video/Image Container */}
      <div className="brand-hero-section__bg-container">
        <div className="brand-hero-section__hero-media">
          <video
            ref={videoRef}
            className="brand-hero-section__hero-video"
            autoPlay
            muted
            loop
            playsInline
          >
            <source src={heroVideoSrc} type="video/mp4" />
          </video>
          <img
            src={heroImageSrc}
            alt={t('pages/home:hero.backgroundAlt')}
            className="brand-hero-section__hero-image"
          />
          <div className="brand-hero-section__gradient-overlay" />
        </div>
        
        <VolumeButton onClick={handleVolumeToggle} isMuted={isMuted} />
        
        <div className="brand-hero-section__journey-text">
          <h1>{String(heroTitle || t('pages/home:hero.title'))}</h1>
        </div>
      </div>
    </section>
  );
};